import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import ExpandableDataTable from "components/admin/ExpandableDataTable";
import StatusBadge from "components/admin/StatusBadge";
import { LivePulse } from "components/markets/MarketLifecycle";
import MarketActionPanel from "components/markets/MarketActionPanel";
import { STATUS_TABS, statusLabel, statusTone } from "components/markets/marketActions";
import MarketFilters, { ORIGIN_LABEL, useMarketFilters } from "components/markets/MarketFilters";
import { apiGet } from "lib/apiClient";
import usePortalAudience, { createdByLabel, usePartnerNames } from "components/markets/usePortalAudience";
import useMarketCounts from "components/markets/useMarketCounts";
import { BUTTON, DetailActions, DetailField, DetailGrid, DetailSection, IdValue } from "components/admin/DetailPanel";
import { DEFAULT_MARKET_CREATION_PAUSED_MESSAGE, PausedNotice } from "components/admin/PlatformNotices";
import { usePlatformStatus } from "contexts/PlatformStatusContext";
import usePaginatedResource from "hooks/usePaginatedResource";
import HomepageToggle from "components/markets/HomepageToggle";
import HowItWorksModal from "./HowItWorksModal";

const columnHelper = createColumnHelper();
const COUNTED_STATUSES = ["PENDING_APPROVAL", "RESULT_PROPOSED"];
const ALL_STATUSES = STATUS_TABS.filter((t) => t.value).map((t) => t.value);
const ORIGIN_TONE = { MANUAL: "gray", IMPORT: "blue", FEED: "amber", AUTOMATION: "gray" };

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "Not set";
}

export default function MarketsPage() {
  const navigate = useNavigate();
  const filterState = useMarketFilters();
  const status = filterState.filters.statuses && !filterState.filters.statuses.includes(",") ? filterState.filters.statuses : "";
  const audience = usePortalAudience();
  const canManage = audience.canManage;
  const partnerNames = usePartnerNames(audience.isTenant);
  const { marketCreation } = usePlatformStatus();
  const creationPaused = marketCreation ? marketCreation.enabled === false : false;
  const creationPausedMessage = marketCreation?.message || DEFAULT_MARKET_CREATION_PAUSED_MESSAGE;
  const { items, total, loading, error, page, setPage, search, setSearch, pageSize, setPageSize, refetch, exportAll } = usePaginatedResource("/portal/markets", {
    pageSize: 20,
    itemsKey: "markets",
    extraParams: filterState.apiParams,
  });

  const [filterOptions, setFilterOptions] = useState({});
  const [clusters, setClusters] = useState([]);
  useEffect(() => {
    apiGet("/portal/markets/filter-options")
      .then(setFilterOptions)
      .catch(() => setFilterOptions({}));
    apiGet("/portal/clusters")
      .then((res) => setClusters(res.clusters || []))
      .catch(() => setClusters([]));
  }, []);
  const partnerList = useMemo(() => Object.entries(partnerNames).map(([id, name]) => ({ id, name })), [partnerNames]);

  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const counts = useMarketCounts(COUNTED_STATUSES, audience.isTenant, reloadKey);
  const approver = audience.isTenant && audience.can("market.approve");
  const resolver = audience.isTenant && audience.can("market.settle");

  function setStatus(next) {
    filterState.setFilter("statuses", next || undefined);
  }

  function afterAction() {
    refetch();
    setReloadKey((k) => k + 1);
  }

  const columns = [
    columnHelper.accessor("question", {
      header: () => <p>MARKET</p>,
      meta: { sortKey: "question", sortFirst: "asc", wrap: true },
      cell: (info) => (
        <div className="flex items-center gap-2">
          {info.row.original.imageUrl && <img src={info.row.original.imageUrl} alt="" className="h-8 w-8 flex-shrink-0 rounded-md object-cover" />}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-navy-700 dark:text-white">{info.getValue()}</p>
            {audience.isTenant && (info.row.original.partnerId || info.row.original.homepageHidden) && (
              <div className="mt-0.5 flex flex-wrap items-center gap-1">
                {info.row.original.partnerId && <StatusBadge label={`Partner market · ${partnerNames[info.row.original.partnerId] || "partner"}`} tone="blue" />}
                {info.row.original.homepageHidden && <StatusBadge label="Off homepage" tone="amber" />}
              </div>
            )}
          </div>
        </div>
      ),
    }),
    columnHelper.accessor("createdAt", {
      header: () => <p>CREATED</p>,
      meta: { sortKey: "createdAt" },
      cell: (info) => {
        const m = info.row.original;
        const who = createdByLabel(m, audience, partnerNames);
        return (
          <div>
            <p className="text-sm text-navy-700 dark:text-white">{formatDateTime(info.getValue())}</p>
            <div className="mt-0.5 flex flex-wrap items-center gap-1">
              <StatusBadge label={who.label} tone={who.tone} />
              {m.origin && <StatusBadge label={ORIGIN_LABEL[m.origin] || m.origin} tone={ORIGIN_TONE[m.origin] || "gray"} />}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("status", {
      header: () => <p>STATUS</p>,
      meta: { sortKey: "status", sortFirst: "asc" },
      cell: (info) => (
        <div className="flex items-center gap-2">
          <LivePulse market={info.row.original} showLabel={false} />
          <StatusBadge label={statusLabel(info.getValue())} tone={statusTone(info.getValue())} />
        </div>
      ),
    }),
    columnHelper.accessor("visibility", {
      header: () => <p>VISIBILITY</p>,
      cell: (info) => <p className="text-sm text-navy-700 dark:text-white">{info.getValue()}</p>,
    }),
    columnHelper.accessor("opensAt", {
      header: () => <p>OPENS</p>,
      meta: { sortKey: "opensAt", sortFirst: "asc" },
      cell: (info) => <p className="text-sm text-navy-700 dark:text-white">{new Date(info.getValue()).toLocaleDateString()}</p>,
    }),
    columnHelper.accessor("closesAt", {
      header: () => <p>CLOSES</p>,
      meta: { sortKey: "closesAt", sortFirst: "asc" },
      cell: (info) => <p className="text-sm text-navy-700 dark:text-white">{new Date(info.getValue()).toLocaleDateString()}</p>,
    }),
  ];

  function renderMarketPanel(m) {
    const who = createdByLabel(m, audience, partnerNames);
    return (
      <div>
        <DetailSection title="Market">
          <DetailGrid>
            <DetailField label="Market id">
              <IdValue value={m.id} />
            </DetailField>
            <DetailField label="Slug">{m.slug}</DetailField>
            <DetailField label="Created by">
              <StatusBadge label={who.label} tone={who.tone} />
            </DetailField>
            <DetailField label="Question" span>
              {m.question}
            </DetailField>
            <DetailField label="Status">
              <StatusBadge label={statusLabel(m.status)} tone={statusTone(m.status)} />
            </DetailField>
            <DetailField label="Visibility">{m.visibility}</DetailField>
            <DetailField label="Opens">{m.opensAt ? new Date(m.opensAt).toLocaleString() : "Not set"}</DetailField>
            <DetailField label="Closes">{m.closesAt ? new Date(m.closesAt).toLocaleString() : "Not set"}</DetailField>
          </DetailGrid>
        </DetailSection>

        {audience.isTenant && (
          <DetailSection title="Owner and homepage" description="Partner markets are listed on the partner's page and, unless you pull them, on your homepage and partner shelves.">
            <DetailGrid>
              <DetailField label="Owner">{m.partnerId ? `Partner: ${partnerNames[m.partnerId] || "unnamed partner"}` : "You (a main market)"}</DetailField>
              <DetailField label="Homepage" span>
                <HomepageToggle marketId={m.id} hidden={m.homepageHidden} canEdit={canManage} onChanged={refetch} />
              </DetailField>
            </DetailGrid>
          </DetailSection>
        )}

        <DetailSection title="Where this market came from">
          <DetailGrid>
            <DetailField label="Created">{formatDateTime(m.createdAt)}</DetailField>
            <DetailField label="Created by">{who.label}</DetailField>
            <DetailField label="How">{ORIGIN_LABEL[m.origin] || "Created by hand"}</DetailField>
            {m.originFileName && <DetailField label="From file">{m.originFileName}</DetailField>}
            <DetailField label="Source">{m.sourceProvider}</DetailField>
            <DetailField label="Last changed">{formatDateTime(m.updatedAt)}</DetailField>
          </DetailGrid>
        </DetailSection>

        <DetailSection
          title={audience.isTenant ? "What this market earns" : "What you earn from this market"}
          description="Commission is 10% of each trader's net profit in the market, taken once at settlement. A trader who ends the market down pays none, and there is no per-trade fee."
        >
          <p className="overflow-x-auto whitespace-nowrap rounded-lg bg-gray-50 px-3 py-2 font-mono text-[11px] text-gray-600 dark:bg-white/5 dark:text-gray-300">
            your payout = (gross revenue − deductions) × your share %
          </p>
        </DetailSection>

        <DetailActions label={canManage ? "Actions at this status" : "Actions"}>
          <button type="button" className={BUTTON.primary} onClick={() => navigate(`/portal/markets/${m.id}`)}>
            {canManage ? "Open market page" : "Performance"}
          </button>
          {audience.isTenant && (
            <button type="button" className={BUTTON.outline} onClick={() => navigate(`/portal/money-timeline?marketId=${m.id}`)}>
              Money timeline
            </button>
          )}
        </DetailActions>
        {canManage && (
          <div className="mt-3">
            <MarketActionPanel
              market={m}
              audience={audience}
              onChanged={afterAction}
              emptyMessage="Nothing to do at this status. Open the market page for results, publishing and payouts."
            />
          </div>
        )}
      </div>
    );
  }

  const tabCount = (value) => (audience.isTenant && COUNTED_STATUSES.includes(value) ? counts[value] : null);

  return (
    <div>
      {canManage && creationPaused && (
        <div className="mb-4">
          <PausedNotice message={creationPausedMessage} />
        </div>
      )}

      {audience.isTenant && (approver || resolver) && (counts.PENDING_APPROVAL > 0 || counts.RESULT_PROPOSED > 0) && (
        <div className="mb-4 flex flex-wrap gap-3">
          {approver && counts.PENDING_APPROVAL > 0 && (
            <button
              type="button"
              onClick={() => setStatus("PENDING_APPROVAL")}
              className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-left dark:border-amber-400/20 dark:bg-amber-400/10"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">Awaiting your approval</p>
              <p className="mt-0.5 text-lg font-semibold text-navy-700 dark:text-white">{counts.PENDING_APPROVAL}</p>
            </button>
          )}
          {resolver && counts.RESULT_PROPOSED > 0 && (
            <button
              type="button"
              onClick={() => setStatus("RESULT_PROPOSED")}
              className="rounded-sm border border-blue-200 bg-blue-50 px-4 py-3 text-left dark:border-blue-400/20 dark:bg-blue-400/10"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">Results to confirm</p>
              <p className="mt-0.5 text-lg font-semibold text-navy-700 dark:text-white">{counts.RESULT_PROPOSED}</p>
            </button>
          )}
        </div>
      )}

      <div className="mb-4 flex gap-5 overflow-x-auto border-b border-border" role="tablist" aria-label="Filter markets by status">
        {STATUS_TABS.map((tab) => {
          const active = status === tab.value;
          const count = tabCount(tab.value);
          const label = audience.isTenant && tab.value === "PENDING_APPROVAL" && approver ? "Awaiting your approval" : tab.label;
          return (
            <button
              key={tab.value || "all"}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setStatus(tab.value)}
              className={`relative flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md py-2 text-sm transition-colors after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-full after:transition-colors after:duration-300 ${
                active ? "font-semibold text-foreground after:bg-primary" : "font-medium text-foreground/65 after:bg-transparent hover:text-foreground"
              }`}
            >
              {label}
              {count > 0 && (
                <span className="rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium leading-none text-amber-700 dark:text-amber-300">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      <ExpandableDataTable
        title={audience.isTenant ? "Markets" : "Your markets"}
        subtitle={
          audience.isTenant
            ? "Every market on your platform, including those your partners created. Open one for its record and the actions its status allows."
            : "Open a market for its full record, what you earn from it, and every action available at its current status."
        }
        renderDetail={(m) => renderMarketPanel(m)}
        columns={columns}
        data={items}
        loading={loading}
        error={error}
        onRetry={refetch}
        emptyMessage={
          filterState.activeCount > 0
            ? "No markets match these filters."
            : audience.isTenant
              ? "No markets yet."
              : "No markets are currently assigned to your organization."
        }
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search markets…"
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        exportFilename="markets"
        onExportAll={exportAll}
        serverSort={filterState.sort}
        onServerSortChange={filterState.setSort}
        filterBar={
          <MarketFilters
            state={filterState}
            statuses={ALL_STATUSES}
            statusLabel={statusLabel}
            options={{ ...filterOptions, clusters, partners: audience.isTenant ? partnerList : [] }}
            fields={{ partner: audience.isTenant }}
          />
        }
        headerActions={
          canManage && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowHowItWorks(true)}
                aria-label="How market creation works"
                className="flex h-9 w-9 items-center justify-center rounded-sm border border-gray-200 text-sm font-semibold text-navy-700 transition hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
              >
                ?
              </button>
              {audience.isTenant && (
                <button
                  onClick={() => navigate("/portal/markets/from-source")}
                  disabled={creationPaused}
                  title={creationPaused ? creationPausedMessage : "Pull questions from a configured source"}
                  className="h-9 rounded-sm border border-gray-200 px-4 text-sm font-semibold text-navy-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                >
                  From a source
                </button>
              )}
              <button
                onClick={() => navigate("/portal/markets/new")}
                disabled={creationPaused}
                title={creationPaused ? creationPausedMessage : undefined}
                className="h-9 rounded-sm bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-brand-500"
              >
                + Create market
              </button>
            </div>
          )
        }
      />

      {showHowItWorks && <HowItWorksModal onClose={() => setShowHowItWorks(false)} />}
    </div>
  );
}
